// ---------------------------------------------------------------------------
// Player colour picker.
//
// The swatches default to `PLAYER_COLORS` from the shared package, which is the
// same list the server draws from when it assigns a colour to a joining player.
// Offering a colour the server never assigns, or hiding one it does, is how two
// players end up believing they are the same colour.
//
// Two things this control has to say out loud, because a room of eight guessed
// wrong about both:
//
//   1. **Which colours are gone.** A taken swatch is drawn as a hollow ring
//      rather than a disc and cannot be pressed. Same position, same 44px
//      target, visibly not a choice — a player picking a colour somebody else
//      already wears is the exact confusion the picker exists to prevent.
//   2. **When the choice does not decide anything.** While teams are on, a
//      player wears their team's colour and this one is only what they go back
//      to on their own. `secondary` shrinks the swatches and dims the group so
//      it reads as a preference rather than as the thing on screen.
//
// Swatches are named by hue rather than by index. "Colour 6" tells a screen
// reader nothing a sighted player can act on; "red" does.
//
// **It is a grid rather than a wrapping row.** With sixteen colours a flex wrap
// puts a ragged, differently indented run on the second line and every swatch
// moves when the container changes width by a few pixels. `auto-fit` with a
// 44px floor fills whole rows of equal cells and never produces a target under
// the minimum: the cells stretch instead of a seventeenth column appearing.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import { PLAYER_COLORS } from '@countdown/shared';

/**
 * Hue anchors for naming an OKLCH swatch. Nearest wins, wrapping at 360.
 *
 * One anchor per hue in the shipped palettes, `TEAM_COLORS` included, so no two
 * swatches on offer can come back with the same name. Two swatches both called
 * "teal" would be worse than numbering them, and `ColorPicker.test.tsx` measures
 * exactly that: twenty colours, twenty names.
 *
 * Twelve anchors were enough for eight player colours and are not enough for
 * sixteen — the ramp is roughly 15 degrees wide now, so anchors sit on the
 * shipped hues rather than between them. A palette edit that moves a hue past
 * its neighbour is what the uniqueness test is there to catch.
 */
const HUE_NAMES: readonly { hue: number; name: string }[] = [
  { hue: 3, name: 'crimson' },
  { hue: 25, name: 'red' },
  { hue: 47, name: 'orange' },
  { hue: 62, name: 'amber' },
  { hue: 90, name: 'gold' },
  { hue: 122, name: 'olive' },
  { hue: 134, name: 'sage' },
  { hue: 155, name: 'green' },
  { hue: 176, name: 'teal' },
  { hue: 199, name: 'aqua' },
  { hue: 212, name: 'steel' },
  { hue: 228, name: 'sky' },
  { hue: 250, name: 'blue' },
  { hue: 271, name: 'indigo' },
  { hue: 285, name: 'violet' },
  { hue: 297, name: 'purple' },
  { hue: 311, name: 'orchid' },
  { hue: 323, name: 'magenta' },
  { hue: 337, name: 'pink' },
  { hue: 350, name: 'rose' },
];

const OKLCH_HUE = /oklch\(\s*[\d.]+%?\s+[\d.]+%?\s+([\d.]+)/i;

/** A name a player can say out loud, or a numbered fallback for anything else. */
export function colorName(color: string, index: number): string {
  const match = OKLCH_HUE.exec(color);
  if (match === null) return `colour ${index + 1}`;

  const hue = ((Number(match[1]) % 360) + 360) % 360;
  let best = HUE_NAMES[0];
  let bestGap = Number.POSITIVE_INFINITY;
  for (const entry of HUE_NAMES) {
    const raw = Math.abs(entry.hue - hue);
    const gap = Math.min(raw, 360 - raw);
    if (gap < bestGap) {
      bestGap = gap;
      best = entry;
    }
  }
  return best.name;
}

export function ColorPicker(props: {
  value: string;
  onChange: (color: string) => void;
  colors?: readonly string[];
  label?: string;
  /**
   * Colours other players already wear. Drawn hollow and refused, so the room
   * cannot end up with two of the same.
   */
  taken?: readonly string[];
  /** The choice is not what anybody sees right now. Smaller, dimmer, still there. */
  secondary?: boolean;
  disabled?: boolean;
}): ReactElement {
  const {
    value,
    onChange,
    colors = PLAYER_COLORS,
    label = 'your colour',
    taken = [],
    secondary = false,
    disabled = false,
  } = props;

  const takenSet = new Set(taken);

  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-secondary={secondary || undefined}
      className={`grid gap-1 transition-opacity duration-200 ease-out [grid-template-columns:repeat(auto-fit,minmax(44px,1fr))] ${
        secondary ? 'opacity-70' : 'opacity-100'
      }`}
    >
      {colors.map((color, index) => {
        const selected = color === value;
        // Your own colour never counts as taken, however the caller built the
        // list: a picker that refuses the swatch it is sitting on is a bug.
        const isTaken = !selected && takenSet.has(color);
        const name = colorName(color, index);
        const off = disabled || isTaken;

        return (
          <button
            key={color}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={isTaken ? `${name}, taken` : name}
            aria-disabled={off || undefined}
            disabled={off}
            title={isTaken ? `${name}, taken` : name}
            data-color-taken={isTaken || undefined}
            onClick={() => onChange(color)}
            // The target stays at least 44px whatever the swatch inside it is
            // doing, so neither `secondary` nor a taken swatch moves the row.
            className={`grid h-11 w-full min-w-11 place-items-center rounded-chip ${
              off ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <span
              aria-hidden="true"
              className="h-[30px] w-[30px] rounded-full transition-[transform,box-shadow,background-color] duration-150 ease-out"
              style={{
                backgroundColor: isTaken ? 'transparent' : color,
                // Size carries the state as well as the fill does, so the three
                // states are still apart from each other in greyscale.
                transform: isTaken
                  ? 'scale(0.7)'
                  : selected
                    ? 'scale(1)'
                    : secondary
                      ? 'scale(0.76)'
                      : 'scale(0.92)',
                // A gap ring, so the selection reads on a swatch whose own hue is
                // close to the ring colour.
                boxShadow: isTaken
                  ? `inset 0 0 0 3px ${color}`
                  : selected
                    ? '0 0 0 2px var(--c-bg), 0 0 0 6px var(--c-blue-200)'
                    : undefined,
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
